import { Box, Center, Image, Link, Text, VStack } from '@chakra-ui/react';
import type { MentionType as MentionTypeEnum } from '@fluenceur/types';
import { MentionMediaType, MentionPlatform, MentionType } from '@fluenceur/types';

import React from 'react';
import ReactPlayer from 'react-player';
import { Clock } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import PlayerVideoButton from './PlayerVideoButton';

interface MentionMediaDisplayProps {
  mediaUrl: string | null;
  mediaType: MentionMediaType;
  isExpired: boolean;
  mentionType: MentionTypeEnum;
  permalink?: string | null;
  platform?: string;
  hostedThumbnailUrl?: string | null;
  /** Cover of a video whose file the platform withholds. */
  coverUrl?: string | null;
  onMediaError?: () => void;
}

/**
 * Extract TikTok video ID from a permalink URL.
 */
function extractTikTokVideoId(url: string): string | null {
  const match = url.match(/\/video\/(\d+)/);
  return match?.[1] || null;
}

/**
 * Component for displaying mention media (video/image) with expired state handling
 */
export const MentionMediaDisplay: React.FC<MentionMediaDisplayProps> = ({
  mediaUrl,
  mediaType,
  isExpired,
  mentionType,
  permalink,
  platform,
  hostedThumbnailUrl,
  coverUrl,
  onMediaError,
}) => {
  const { t } = useTranslation();
  const isVideo = mediaType === MentionMediaType.VIDEO;

  if (isExpired) {
    const isStory = mentionType === MentionType.STORY;
    const description = isStory
      ? t('mentions.media.storyExpiredDescription')
      : t('mentions.media.postExpiredDescription');

    if (hostedThumbnailUrl) {
      return (
        <Box position="relative" w="100%" h="100%">
          <Image
            src={hostedThumbnailUrl}
            alt={t('mentions.media.thumbnailAlt')}
            w="100%"
            h="100%"
            objectFit="contain"
            filter="brightness(0.45)"
          />
          <VStack
            position="absolute"
            inset={0}
            gap={2}
            color="white"
            textAlign="center"
            p={6}
            justify="center"
            pointerEvents="none"
          >
            <Clock width={40} height={40} color="rgba(255,255,255,0.85)" />
            <Text fontSize="md" fontWeight="semibold">
              {t('mentions.media.expired')}
            </Text>
            <Text fontSize="xs" color="whiteAlpha.800" maxW="260px">
              {description}
            </Text>
          </VStack>
        </Box>
      );
    }

    return (
      <VStack gap={2} color="white" textAlign="center" p={8}>
        <Clock width={64} height={64} color="rgba(156,163,175,1)" />
        <Text fontSize="lg" fontWeight="semibold">
          {t('mentions.media.expired')}
        </Text>
        <Text fontSize="sm" color="gray.300" maxW="300px">
          {description}
        </Text>
      </VStack>
    );
  }

  if (!mediaUrl) {
    const stillUrl = hostedThumbnailUrl ?? coverUrl ?? null;
    if (!stillUrl) {
      return null;
    }
    // A reel whose file Meta withholds: its cover here, the video on Instagram.
    return (
      <Box position="relative" w="100%" h="100%">
        <Image
          src={stillUrl}
          alt={t('mentions.media.mediaAlt')}
          w="100%"
          h="100%"
          objectFit="contain"
          onError={onMediaError}
        />
        {/* Centred, where the caption overlay never reaches: the play mark
            opens the reel on its platform, the only place it plays. */}
        {permalink && (
          <Center position="absolute" inset={0} pointerEvents="none">
            <Link
              href={permalink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t(`mentions.preview.viewOn.${platform ?? 'instagram'}`)}
              pointerEvents="auto"
            >
              <PlayerVideoButton size="lg" />
            </Link>
          </Center>
        )}
      </Box>
    );
  }

  /* TikTok: embed player */
  if (platform === MentionPlatform.TIKTOK && permalink) {
    const videoId = extractTikTokVideoId(permalink);
    if (videoId) {
      return (
        <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center">
          <iframe
            src={`https://www.tiktok.com/player/v1/${videoId}?music_info=0&description=0&rel=0&autoplay=1&loop=1&controls=0&native_context_menu=0`}
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '400px',
              border: 'none',
            }}
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="TikTok video"
          />
        </Box>
      );
    }
  }

  /* Video (Instagram etc.) */
  if (isVideo) {
    return (
      <ReactPlayer
        url={mediaUrl}
        width="100%"
        height="100%"
        playing
        controls
        muted
        pip
        onError={onMediaError}
        style={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
        }}
        config={{
          file: {
            attributes: {
              style: {
                objectFit: 'contain',
                outline: 'none',
                width: '100%',
                height: '100%',
              },
            },
          },
        }}
      />
    );
  }

  return (
    <Image
      src={mediaUrl}
      alt={t('mentions.media.mediaAlt')}
      w="100%"
      h="100%"
      objectFit="contain"
      onError={onMediaError}
    />
  );
};
